import React, { useState, useEffect } from 'react';
import { Check, CreditCard, AlertCircle, Calendar, Loader } from 'lucide-react';
import './SubscriptionManagement.css';
import { apiService } from '../services/api';

interface SubscriptionData {
  status: string;
  plan: string;
  trialEndsAt?: string;
  subscriptionEndsAt?: string;
  isCanceled: boolean;
  hasStripeCustomer: boolean;
}

const SubscriptionManagement: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const data = await apiService.getSubscription();
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { url } = await apiService.createPortalSession();
      window.location.href = url;
    } catch (error: any) {
      console.error('Error creating portal session:', error);
      alert(error.response?.data?.error || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-loading">
        <Loader className="spinner" size={32} />
        <p>Loading subscription...</p>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="subscription-error">
        <AlertCircle size={32} />
        <p>Failed to load subscription information</p>
      </div>
    );
  }

  const planFeatures = {
    free: [
      '50 contacts',
      'Basic CRM features',
      'Text message reminders',
      'Email support',
    ],
    pro: [
      'Unlimited contacts',
      'All CRM features',
      'Advanced analytics',
      'Custom fields',
      'Priority support',
    ],
    enterprise: [
      'Everything in Pro',
      'Team collaboration',
      'API access',
      'Dedicated support',
      'Custom integrations',
    ],
  };

  const isTrialing = subscription.status === 'trialing';
  const isActive = subscription.status === 'active' || isTrialing;
  const isPastDue = subscription.status === 'past_due';

  return (
    <div className="subscription-container">
      <div className="subscription-header">
        <h1>Your Subscription</h1>
        <p>Manage your billing and subscription settings</p>
      </div>

      {/* Current Plan Card */}
      <div className="current-plan-card">
        <div className="plan-info">
          <div className="plan-badge">
            <span className="plan-name">{subscription.plan.toUpperCase()}</span>
            {isTrialing && <span className="trial-badge">Free Trial</span>}
            {subscription.isCanceled && <span className="canceled-badge">Canceled</span>}
            {isPastDue && <span className="past-due-badge">Past Due</span>}
          </div>

          <div className="plan-details">
            {isTrialing && subscription.trialEndsAt && (
              <div className="detail-item">
                <Calendar size={16} />
                <span>
                  Trial ends {new Date(subscription.trialEndsAt).toLocaleDateString()}
                </span>
              </div>
            )}

            {isActive && !isTrialing && subscription.subscriptionEndsAt && (
              <div className="detail-item">
                <Calendar size={16} />
                <span>
                  {subscription.isCanceled ? 'Access until' : 'Renews on'}{' '}
                  {new Date(subscription.subscriptionEndsAt).toLocaleDateString()}
                </span>
              </div>
            )}

            {isPastDue && (
              <div className="detail-item error">
                <AlertCircle size={16} />
                <span>Payment failed. Please update your payment method.</span>
              </div>
            )}
          </div>

          <div className="plan-features">
            <h3>Your Plan Includes:</h3>
            <ul>
              {planFeatures[subscription.plan as keyof typeof planFeatures]?.map(
                (feature, index) => (
                  <li key={index}>
                    <Check size={16} />
                    <span>{feature}</span>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>

        <div className="plan-actions">
          {subscription.hasStripeCustomer && (
            <button
              className="manage-button primary"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              <CreditCard size={18} />
              {portalLoading ? 'Loading...' : 'Manage Billing'}
            </button>
          )}

          {subscription.plan === 'free' && (
            <a href="/pricing" className="upgrade-button">
              Upgrade to Pro
            </a>
          )}
        </div>
      </div>

      {/* Information Cards */}
      <div className="info-cards">
        <div className="info-card">
          <h3>Billing Portal</h3>
          <p>
            Update your payment method, download invoices, and view your billing history in
            the Stripe Customer Portal.
          </p>
        </div>

        <div className="info-card">
          <h3>Cancel Anytime</h3>
          <p>
            You can cancel your subscription at any time. You'll have access until the end
            of your billing period.
          </p>
        </div>

        <div className="info-card">
          <h3>Need Help?</h3>
          <p>
            Have questions about your subscription? Contact our support team at{' '}
            <a href="mailto:support@itsremi.ai">support@itsremi.ai</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;

